import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Table,
  Tbody,
  Tr,
  Td,
  Button,
  Icon,
  Flex,
  Heading,
  Image,
} from '@chakra-ui/react';
import { MdPending, MdPlayCircle } from 'react-icons/md';
import axios from 'axios';
import RelojTiempoReal from 'components/Reloj/reloj';

interface Turno {
  ID: number;
  Folio: string;
  Area: string;
  FechaCreacion: string;
  Estado: string;
  UsuarioAsignado: string | null;
  Notas: string | null;
  FechaAtencion: string | null;
  CajaID: number | null;
}

interface TurnosData {
  proximosTurnos: Turno[];
  turnosEnAtencion: Turno[];
}

const TurnosRealTime = () => {
  const [turnosPendientes, setTurnosPendientes] = useState<string[]>([]);
  const [turnosAtendidos, setTurnosAtendidos] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [audioActivado, setAudioActivado] = useState(false);

  // Usar useRef para que se actualicen inmediatamente (sincrónicamente)
  const turnosAnunciadosRef = useRef<Set<string>>(new Set());
  const turnosAtendidosAnterioresRef = useRef<{ [key: string]: string }>({});

  const [audioContextRef] = useState<AudioContext | null>(() => {
    try {
      return new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch {
      return null;
    }
  });

  // Función para activar el audio manualmente
  const activarAudio = async () => {
    if (audioContextRef && audioContextRef.state === 'suspended') {
      try {
        await audioContextRef.resume();
        setAudioActivado(true);
        console.log('AudioContext activado manualmente');

        // Reproducir un sonido de prueba
        const oscillator = audioContextRef.createOscillator();
        const gainNode = audioContextRef.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.destination);
        oscillator.frequency.value = 600;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContextRef.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.currentTime + 0.3);
        oscillator.start(audioContextRef.currentTime);
        oscillator.stop(audioContextRef.currentTime + 0.3);

        // Prueba de voz
        setTimeout(() => {
          const utterance = new SpeechSynthesisUtterance('Audio activado');
          utterance.lang = 'es-MX';
          window.speechSynthesis.speak(utterance);
        }, 500);
      } catch (error) {
        console.error('Error al activar AudioContext:', error);
      }
    } else if (audioContextRef?.state === 'running') {
      setAudioActivado(true);
      console.log('AudioContext ya está activo');
    }
  };

  // Inicializar audio context al hacer clic en cualquier parte de la pantalla
  useEffect(() => {
    const inicializarAudio = async () => {
      await activarAudio();
    };

    // Activar audio con el primer clic
    document.addEventListener('click', inicializarAudio, { once: true });

    return () => {
      document.removeEventListener('click', inicializarAudio);
    };
  }, [audioContextRef]);

  // Función para reproducir sonido de notificación y anunciar el turno
  const reproducirSonido = async (turnoFolio: string) => {
    try {
      console.log('Intentando reproducir sonido para turno:', turnoFolio);

      if (!audioContextRef) {
        console.error('AudioContext no disponible');
        return;
      }

      // Asegurar que el AudioContext esté activo
      if (audioContextRef.state === 'suspended') {
        await audioContextRef.resume();
      }

      // Crear y reproducir el tono
      const oscillator = audioContextRef.createOscillator();
      const gainNode = audioContextRef.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.destination);

      // Configurar el sonido (tono de campana)
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      // Configurar el volumen con fade out
      const currentTime = audioContextRef.currentTime;
      gainNode.gain.setValueAtTime(0.5, currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + 1);

      // Reproducir sonido
      oscillator.start(currentTime);
      oscillator.stop(currentTime + 1);

      console.log('Tono reproducido');

      // Usar Speech Synthesis para anunciar el turno
      setTimeout(() => {
        const mensaje = `Turno ${turnoFolio}`;
        console.log('Anunciando:', mensaje);

        const utterance = new SpeechSynthesisUtterance(mensaje);
        utterance.lang = 'es-MX'; // Español de México
        utterance.rate = 0.8;
        utterance.pitch = 1;
        utterance.volume = 1;

        // Escuchar eventos para debug
        utterance.onstart = () => console.log('Inicio de voz');
        utterance.onend = () => console.log('Fin de voz');
        utterance.onerror = (e) => console.error('Error en voz:', e);

        window.speechSynthesis.cancel(); // Cancelar cualquier anuncio previo
        window.speechSynthesis.speak(utterance);
      }, 1200);
    } catch (error) {
      console.error('Error al reproducir sonido:', error);
    }
  };

  // Función para obtener los turnos desde la API
  const fetchTurnos = async () => {
    setLoading(true);
    try {
      const response = await axios.get<TurnosData>(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/turnos`);

      if (response.data) {
        const { proximosTurnos, turnosEnAtencion } = response.data;

        // Extraer solo los folios de los turnos pendientes
        const pendientes: string[] = proximosTurnos.map((turno) => turno.Folio);

        // Crear objeto de turnos en atención { Area: Folio }
        const atendidos: { [key: string]: string } = {};
        turnosEnAtencion.forEach((turno) => {
          atendidos[turno.CajaID] = turno.Folio;
        });

        // Detectar si hay nuevos turnos en atención
        const cajasNuevas = Object.keys(atendidos).filter(
          (caja) => !turnosAtendidosAnterioresRef.current[caja] || turnosAtendidosAnterioresRef.current[caja] !== atendidos[caja]
        );

        console.log('Turnos anteriores:', turnosAtendidosAnterioresRef.current);
        console.log('Turnos actuales:', atendidos);
        console.log('Cajas nuevas detectadas:', cajasNuevas);
        console.log('Tiene turnos anteriores?', Object.keys(turnosAtendidosAnterioresRef.current).length > 0);

        // Si hay nuevos turnos en atención, reproducir sonido y anunciar cada turno nuevo
        // Solo anunciar si ya pasó la primera carga (para no anunciar todo lo que ya estaba en atención)
        if (cajasNuevas.length > 0) {
          console.log('✅ SE DETECTARON CAMBIOS, VERIFICANDO SI YA FUERON ANUNCIADOS...');

          // Filtrar solo los turnos que NO han sido anunciados
          const turnosNuevosParaAnunciar = cajasNuevas.filter(caja => {
            const turnoFolio = atendidos[caja];
            const claveUnica = `${caja}-${turnoFolio}`;
            const yaAnunciado = turnosAnunciadosRef.current.has(claveUnica);
            console.log(`  Caja ${caja} - Turno ${turnoFolio} - Ya anunciado: ${yaAnunciado}`);
            return !yaAnunciado;
          });

          // Solo anunciar si ya hubo una carga previa (evitar anunciar en primera carga)
          const esPrimeraCarga = Object.keys(turnosAtendidosAnterioresRef.current).length === 0;

          if (turnosNuevosParaAnunciar.length > 0) {
            if (esPrimeraCarga) {
              console.log('🔇 Es la primera carga, marcando turnos pero sin anunciar');
              // Marcar todos como anunciados sin reproducir sonido
              turnosNuevosParaAnunciar.forEach(caja => {
                const turnoFolio = atendidos[caja];
                const claveUnica = `${caja}-${turnoFolio}`;
                turnosAnunciadosRef.current.add(claveUnica);
              });
            } else {
              console.log('📢 Anunciando turnos nuevos:', turnosNuevosParaAnunciar);

              // Marcar TODOS como anunciados INMEDIATAMENTE (antes de reproducir)
              const nuevasClaves = turnosNuevosParaAnunciar.map(caja => {
                const turnoFolio = atendidos[caja];
                return `${caja}-${turnoFolio}`;
              });

              // Agregar al Set de manera inmediata (useRef se actualiza sincrónicamente)
              nuevasClaves.forEach(clave => {
                turnosAnunciadosRef.current.add(clave);
              });
              console.log('🔖 Marcados como anunciados:', nuevasClaves);
              console.log('🔖 Total anunciados:', turnosAnunciadosRef.current.size);

              // Luego reproducir sonidos con delay
              turnosNuevosParaAnunciar.forEach((caja, index) => {
                const turnoFolio = atendidos[caja];

                setTimeout(() => {
                  console.log(`Reproduciendo sonido para caja ${caja}: ${turnoFolio}`);
                  reproducirSonido(turnoFolio);
                }, index * 3000); // Esperar 3 segundos entre cada anuncio si hay múltiples
              });
            }
          } else {
            console.log('⏭️ Todos los turnos ya fueron anunciados');
          }
        } else {
          console.log('No hay cambios en turnos de atención');
        }

        // Actualizar los estados
        setTurnosPendientes(pendientes);
        setTurnosAtendidos(atendidos);
        turnosAtendidosAnterioresRef.current = atendidos; // Actualizar ref inmediatamente
      }
    } catch (error) {
      console.error("Error al obtener los turnos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTurnos();
    const interval = setInterval(fetchTurnos, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      p={6}
      bg="white"
      color="black"
      borderRadius="lg"
      boxShadow="2xl"
      w="100vw"
      h="100vh"
      mx="auto"
      textAlign="center"
    >
      {/* Botón para activar audio (visible si no está activado) */}
      {!audioActivado && (
        <Button
          position="fixed"
          top="10px"
          right="10px"
          colorScheme="red"
          size="lg"
          onClick={activarAudio}
          zIndex={9999}
          boxShadow="lg"
          _hover={{ transform: 'scale(1.05)' }}
        >
          🔊 Activar Audio
        </Button>
      )}

      {/* Cajas de Atención */}
      <HStack
        spacing={75} // Ajustamos el espaciado a un valor más pequeño
        mb={2} // Reducimos el margen inferior para optimizar el diseño
        justifyContent="flex-start" // Alinea los elementos a la izquierda, cerca del logo
        alignItems="center"
        w="100%"
      >
        {/* Logo */}
        <Box>
          {/* Logo */}
          <Image
            src="https://totolac.gob.mx/contenidos/totolac/img/banner_2024_10_7_1728346730.2712.jpg"
            alt="Logo Totolac"
            boxSize="140px"
          />

          {/* Reloj en tiempo real */}
          <RelojTiempoReal />
        </Box>

        {/* Cajas de Atención */}
        <VStack spacing={2} align="center">
          {/* Leyenda Turnos en Atención */}
          <Text fontSize="2xl" fontWeight="bold" color="red.700">
            🔴 Turnos en Atención
          </Text>

          {/* Cajas de atención */}
          <HStack spacing={4} justifyContent="center">
            {Object.entries(turnosAtendidos).map(([CajaID, turno]) => (
              <Box
                key={CajaID}
                p={4}
                bg="red.700"
                color="white"
                borderRadius="lg"
                boxShadow="lg"
                textAlign="center"
                minW="150px"
              >
                <Text fontSize="xl" fontWeight="bold">
                  Caja: {CajaID}
                </Text>
                <Text fontSize="3xl" fontWeight="bold">
                  {turno}
                </Text>
              </Box>
            ))}
          </HStack>
        </VStack>
      </HStack>

      <Box display="flex" width="100%" alignItems="center">
        {/* Turnos Pendientes */}
        <Box
          flex="0.1"
          p={6}
          borderRadius="lg"
          boxShadow="lg"
          textAlign="center"
          border="2px solid black"
          bg="gray.100"
        >
          <HStack justifyContent="center" mb={4}>
            <Icon as={MdPending} boxSize={6} color="red.700" />
            <Text fontSize="2xl" fontWeight="bold" color="red.700">
              Proximos Turnos
            </Text>
          </HStack>
          <Table variant="unstyled" size="md" mt={4}>
            <Tbody>
              {turnosPendientes.map((turno, index) => (
                <Tr key={index}>
                  <Td
                    fontSize="2xl"
                    fontWeight="bold"
                    color="black"
                    textAlign="center"
                    borderBottom="1px solid gray"
                  >
                    {turno}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {/* Video de YouTube */}
        <Box flex="0.9" p={6} borderRadius="md" textAlign="center">
          {/* <HStack justifyContent="center" mb={4}>
            <Icon as={MdPlayCircle} boxSize={6} color="black" />
            <Text fontSize="2xl" fontWeight="bold" color="black">Información</Text>
          </HStack> */}
          <iframe
            width="100%"
            // height="calc(100vh - 100px)" // Ajusta dinámicamente según la pantalla
            src="https://www.youtube.com/embed/rZpSnmms0LM?autoplay=1&mute=1&loop=1&playlist=rZpSnmms0LM"

            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            style={{
              display: 'block',
              objectFit: 'cover', // Asegura que el video cubra el espacio sin distorsionarse
              minHeight: '65vh', // Ocupa toda la pantalla
              width: '100%',
            }}
          ></iframe>
        </Box>
      </Box>

      <Box
        width="100%"
        bg="#5b182b"
        color="white"
        position="fixed"
        bottom="0"
        left="0"
        py={3} // Ajuste de padding para darle mejor apariencia
      >
        <Box textAlign="center" fontSize="sm" color="white.300">
          <Text>© 2026 Ayuntamiento de Totolac.</Text>
        </Box>
      </Box>
    </Box>
  );
};

export default TurnosRealTime;
